/**
 * RunPanel — Trigger a job run, monitor real-time status, and view run history.
 *
 * Design:
 *   - "Run Job" button → POST .../run → status badge updates queued→running→done
 *   - Polls GET .../runs/{run_id} every POLL_INTERVAL_MS while non-terminal
 *   - Run history table with expandable DQ result rows
 *   - "Run All Jobs" button at top → POST .../run-all
 *
 * Status chip colours:
 *   queued    → grey  (bg-white/10 text-white/50 border-white/20)
 *   running   → blue  (bg-blue-500/10 text-blue-400 border-blue-400/20) + animate-pulse
 *   completed → green (bg-emerald-500/10 text-emerald-400 border-emerald-400/20)
 *   failed    → red   (bg-red-500/10 text-red-400 border-red-400/20)
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  Loader2,
  Play,
  PlayCircle,
  GitBranch,
  ScanSearch,
  Square,
} from "lucide-react";
import type {
  PEJob,
  PEJobRun,
  JobRunQueued,
  DQRuleResult,
  RunStatus,
  RunMode,
  SchemaDriftApplyResult,
  SchemaDriftCheck,
  SchemaDriftImpact,
  SchemaDriftImpactApplyResult,
} from "../../../features/pipeline-engineer/types";
import {
  triggerJobRun,
  listJobRuns,
  getJobRun,
  runAllJobs,
  checkSchemaDrift,
  checkSchemaDriftImpact,
  stopJobRun,
} from "../../../features/pipeline-engineer/api/pe-client";
import { runStatusStyle } from "../../../features/pipeline-engineer/runFormatting";
import { SchemaDriftPanel } from "./SchemaDriftPanel";
import { SchemaDriftImpactPanel } from "./SchemaDriftImpactPanel";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const POLL_INTERVAL_MS = 3000;
/** Give up polling after this many consecutive network errors to un-lock the Run Job button. */
const MAX_POLL_ERRORS = 5;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Active (non-terminal) statuses. Everything else stops the poll — including future
 *  backend values like "cancelled" or "timed_out" that aren't in RunStatus yet. */
const ACTIVE_STATUSES = new Set<string>(["queued", "running"]);

function isTerminalStatus(status: string): boolean {
  return !ACTIVE_STATUSES.has(status);
}

function formatDuration(seconds: number | null): string {
  if (seconds === null) return "—";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
}

function formatTimestamp(ts: string): string {
  try {
    return new Date(ts).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return ts;
  }
}

/** A minimal PEJobRun built from a 202 run response, so polling can start at once. */
function queuedRunStub(queued: JobRunQueued): PEJobRun {
  const now = new Date().toISOString();
  return {
    id: queued.runId,
    jobId: queued.jobId,
    status: "queued",
    errorMessage: null,
    rowsExtracted: null,
    rowsLoaded: null,
    rowsMerged: null,
    rowsExpired: null,
    rowsNewVersions: null,
    pushdownSql: null,
    dqResults: [],
    startedAt: now,
    completedAt: null,
    durationSeconds: null,
    createdAt: now,
  };
}

/** A failed run whose error the backend classified as schema drift (SQLSTATE-based). */
function isSchemaDriftError(message: string | null): boolean {
  return (message ?? "").startsWith("Schema drift:");
}

function dqSummary(results: DQRuleResult[]): string {
  if (results.length === 0) return "No DQ rules";
  const passed = results.filter((r) => r.passed).length;
  return `${passed}/${results.length} passed`;
}

// ---------------------------------------------------------------------------
// Status Chip
// ---------------------------------------------------------------------------

interface StatusChipProps {
  status: RunStatus;
}

function StatusChip({ status }: StatusChipProps) {
  const { chip, label } = runStatusStyle(status);
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${chip}`}
      aria-label={`Run status: ${label}`}
    >
      {status === "running" && (
        <span
          className={`h-1.5 w-1.5 rounded-full bg-blue-400 animate-pulse`}
          aria-hidden="true"
        />
      )}
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// DQ Result Row (expandable detail)
// ---------------------------------------------------------------------------

interface DQRowProps {
  result: DQRuleResult;
}

function DQResultRow({ result }: DQRowProps) {
  return (
    <div className="flex items-center gap-3 py-1 text-xs text-white/60 pl-4">
      <span
        className={`w-2 h-2 rounded-full flex-shrink-0 ${result.passed ? "bg-emerald-400" : "bg-red-400"}`}
        aria-hidden="true"
      />
      <span className="font-mono flex-shrink-0 text-white/80">
        {result.ruleName}
      </span>
      <span className="text-white/30">{result.type}</span>
      <span className={result.passed ? "text-emerald-400" : "text-red-400"}>
        {result.passed
          ? "passed"
          : `failed (${result.failingCount < 0 ? "error" : `${result.failingCount} violations`})`}
      </span>
      {result.error !== undefined && (
        <span className="text-red-300/60 truncate">{result.error}</span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Run History Row
// ---------------------------------------------------------------------------

interface HistoryRowProps {
  run: PEJobRun;
  strategy: string;
}

function HistoryRow({ run, strategy }: HistoryRowProps) {
  const [expanded, setExpanded] = useState(false);
  const hasDq = run.dqResults.length > 0;

  return (
    <>
      <tr className="border-t border-white/5 hover:bg-white/3 transition-colors">
        <td className="py-2 px-3 text-xs text-white/50">
          {formatTimestamp(run.startedAt)}
        </td>
        <td className="py-2 px-3 text-xs text-white/60 capitalize">
          {strategy.replace(/_/g, " ")}
        </td>
        <td className="py-2 px-3">
          <StatusChip status={run.status} />
        </td>
        <td className="py-2 px-3 text-xs text-white/60 text-right">
          {run.rowsLoaded ?? "—"}
        </td>
        <td className="py-2 px-3 text-xs text-white/60 text-right">
          {formatDuration(run.durationSeconds)}
        </td>
        <td className="py-2 px-3 text-xs text-white/60">
          <div className="flex items-center gap-1">
            <span>{dqSummary(run.dqResults)}</span>
            {hasDq && (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                aria-label={
                  expanded ? "Collapse DQ results" : "Expand DQ results"
                }
                className="p-0.5 text-white/30 hover:text-white/60 transition-colors"
              >
                {expanded ? (
                  <ChevronDown size={12} aria-hidden="true" />
                ) : (
                  <ChevronRight size={12} aria-hidden="true" />
                )}
              </button>
            )}
          </div>
        </td>
      </tr>
      {(run.status === "failed" || run.status === "cancelled") &&
        run.errorMessage && (
          <tr>
            <td
              colSpan={6}
              className={`px-3 pb-2 text-xs ${run.status === "failed" ? "text-red-400/80" : "text-amber-300/80"}`}
            >
              {run.errorMessage}
            </td>
          </tr>
        )}
      {expanded && hasDq && (
        <tr>
          <td colSpan={6} className="pb-2">
            <div className="space-y-0.5">
              {run.dqResults.map((r, idx) => (
                <DQResultRow key={`${r.ruleName}-${idx}`} result={r} />
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface RunPanelProps {
  pipelineId: string;
  jobId: string;
  job: PEJob;
  /** The job changed on the server (e.g. a schema-drift fix updated its mapping). */
  onJobChanged?: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function RunPanel({
  pipelineId,
  jobId,
  job,
  onJobChanged,
}: RunPanelProps) {
  const [runs, setRuns] = useState<PEJobRun[]>([]);
  const [activeRun, setActiveRun] = useState<PEJobRun | null>(null);
  /** Incremented on poll network-error to re-trigger the polling useEffect. */
  const [pollTick, setPollTick] = useState(0);
  const [triggering, setTriggering] = useState(false);
  const [triggerError, setTriggerError] = useState<string | null>(null);
  const [stopping, setStopping] = useState(false);
  const [runAllPending, setRunAllPending] = useState(false);
  const [runAllMsg, setRunAllMsg] = useState<string | null>(null);
  const [runAllError, setRunAllError] = useState<string | null>(null);
  const [runMode, setRunMode] = useState<RunMode>("parallel");
  const [historyLoading, setHistoryLoading] = useState(true);
  const [driftCheck, setDriftCheck] = useState<SchemaDriftCheck | null>(null);
  const [checkingDrift, setCheckingDrift] = useState(false);
  const [driftMsg, setDriftMsg] = useState<string | null>(null);
  const [driftError, setDriftError] = useState<string | null>(null);
  const [impact, setImpact] = useState<SchemaDriftImpact | null>(null);
  const [checkingImpact, setCheckingImpact] = useState(false);
  /** Consecutive poll failures; cleared on success. At MAX_POLL_ERRORS we abort polling
   *  and re-enable the Run Job button so the user isn't permanently locked out. */
  const pollErrorCountRef = useRef(0);

  // ── Load run history on mount ──────────────────────────────────────────────
  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const history = await listJobRuns(pipelineId, jobId);
      setRuns(history);
      // If the most recent run is still non-terminal, resume polling it
      if (history.length > 0 && !isTerminalStatus(history[0].status)) {
        setActiveRun(history[0]);
      }
    } catch {
      // History load failure is non-critical — show empty table
    } finally {
      setHistoryLoading(false);
    }
  }, [pipelineId, jobId]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  // ── Polling: chained setTimeout (not setInterval) ─────────────────────────
  // The `pollTick` dependency ensures a retry fires after a transient error:
  // on success, `setActiveRun(updated)` changes `activeRun` and re-triggers
  // the effect; on failure, `setPollTick(t => t + 1)` changes `pollTick`
  // and re-triggers the effect.
  // After MAX_POLL_ERRORS consecutive failures we give up, clear `activeRun`
  // (re-enables Run Job button), and surface an error message so the user
  // is never permanently locked out by a backend outage.
  useEffect(() => {
    if (activeRun === null || isTerminalStatus(activeRun.status)) return;

    const timer = setTimeout(async () => {
      try {
        const updated = await getJobRun(pipelineId, jobId, activeRun.id);
        pollErrorCountRef.current = 0; // reset consecutive-error counter on success
        setActiveRun(updated);
        // Merge updated run back into history list
        setRuns((prev) => {
          const idx = prev.findIndex((r) => r.id === updated.id);
          if (idx === -1) return [updated, ...prev];
          const next = [...prev];
          next[idx] = updated;
          return next;
        });
      } catch {
        pollErrorCountRef.current += 1;
        if (pollErrorCountRef.current >= MAX_POLL_ERRORS) {
          // Give up — unlock the Run Job button and inform the user.
          pollErrorCountRef.current = 0;
          setActiveRun(null);
          setTriggerError(
            "Run status polling failed after repeated errors — check backend connectivity. Refresh to retry.",
          );
        } else {
          // Transient error — re-trigger effect to retry after POLL_INTERVAL_MS.
          setPollTick((t) => t + 1);
        }
      }
    }, POLL_INTERVAL_MS);

    return () => clearTimeout(timer);
    // jobId added: if the parent ever re-uses the same RunPanel instance for a
    // different job, the closure must re-capture the new jobId immediately.
  }, [pipelineId, jobId, activeRun, pollTick]);

  // ── Trigger job run ────────────────────────────────────────────────────────
  const handleRunJob = useCallback(async () => {
    const warning =
      job.loadStrategy === "full_replace"
        ? `Run "${job.jobName}" with Full Replace? All rows in "${job.targetTable}" will be replaced.`
        : `Run "${job.jobName}" now?`;
    if (!window.confirm(warning)) return;
    setTriggerError(null);
    setTriggering(true);
    try {
      const queued: JobRunQueued = await triggerJobRun(pipelineId, jobId);
      // Construct a minimal PEJobRun from the 202 response to start polling
      const stub = queuedRunStub(queued);
      setActiveRun(stub);
      setRuns((prev) => [stub, ...prev]);
    } catch (err) {
      setTriggerError(
        err instanceof Error ? err.message : "Failed to trigger run.",
      );
    } finally {
      setTriggering(false);
    }
  }, [pipelineId, jobId, job.jobName, job.loadStrategy, job.targetTable]);

  // ── Stop the current run ───────────────────────────────────────────────────
  // A run that stopped reporting progress comes back failed at once (unlocking
  // Run Job); a live one comes back with stopRequestedAt set and the poll picks
  // up "cancelled" when the runner reaches its next safe point.
  const handleStopRun = useCallback(async () => {
    if (activeRun === null) return;
    if (
      !window.confirm(
        `Stop this run of "${job.jobName}"? If it is still loading, it stops before its next write to "${job.targetTable}" (a write already in progress finishes first). If it has stopped responding, it is marked failed now.`,
      )
    ) {
      return;
    }
    setTriggerError(null);
    setStopping(true);
    try {
      const updated = await stopJobRun(pipelineId, jobId, activeRun.id);
      setActiveRun(updated);
      setRuns((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    } catch (err) {
      setTriggerError(
        err instanceof Error ? err.message : "Failed to stop the run.",
      );
      // 409 = it finished meanwhile: reload so the panel shows how it ended.
      void loadHistory();
    } finally {
      setStopping(false);
    }
  }, [activeRun, pipelineId, jobId, job.jobName, job.targetTable, loadHistory]);

  // ── Run all jobs ───────────────────────────────────────────────────────────
  const handleRunAll = useCallback(async () => {
    if (
      !window.confirm(
        "Run all jobs in this pipeline? Jobs using Full Replace will replace all rows in their target tables.",
      )
    ) {
      return;
    }
    setRunAllError(null);
    setRunAllMsg(null);
    setRunAllPending(true);
    try {
      const result = await runAllJobs(pipelineId, runMode);
      setRunAllMsg(
        `${result.jobsQueued} job${result.jobsQueued !== 1 ? "s" : ""} queued.`,
      );
    } catch (err) {
      setRunAllError(
        err instanceof Error ? err.message : "Failed to run all jobs.",
      );
    } finally {
      setRunAllPending(false);
    }
  }, [pipelineId, runMode]);

  // ── Schema drift: check → approve/reject (SchemaDriftPanel) ───────────────
  const handleCheckDrift = useCallback(async () => {
    setDriftError(null);
    setDriftMsg(null);
    setDriftCheck(null);
    setCheckingDrift(true);
    try {
      const result = await checkSchemaDrift(pipelineId, jobId);
      if (result.drift) {
        setDriftCheck(result);
      } else {
        setDriftMsg(
          `No drift: ${result.sourceTable} and ${result.targetTable} match the job's mapping.`,
        );
      }
    } catch (err) {
      setDriftError(
        err instanceof Error ? err.message : "Failed to check for drift.",
      );
    } finally {
      setCheckingDrift(false);
    }
  }, [pipelineId, jobId]);

  const handleDriftApplied = useCallback(
    (result: SchemaDriftApplyResult) => {
      setDriftCheck(null);
      setDriftMsg(
        `Applied ${result.statements.length} statement${result.statements.length !== 1 ? "s" : ""}; ` +
          `the mapping now has ${result.mappingColumnCount} columns.` +
          (result.run ? " The job was rerun; see Current Run." : ""),
      );
      if (result.run) {
        const stub = queuedRunStub(result.run);
        setActiveRun(stub);
        setRuns((prev) => [stub, ...prev]);
      }
      onJobChanged?.();
    },
    [onJobChanged],
  );

  // ── Impact: every job that reads this source (and downstream) ───────────
  const handleCheckImpact = useCallback(async () => {
    setDriftError(null);
    setDriftMsg(null);
    setDriftCheck(null);
    setImpact(null);
    setCheckingImpact(true);
    try {
      setImpact(await checkSchemaDriftImpact(pipelineId, jobId));
    } catch (err) {
      setDriftError(
        err instanceof Error ? err.message : "Failed to check the impact.",
      );
    } finally {
      setCheckingImpact(false);
    }
  }, [pipelineId, jobId]);

  const handleImpactApplied = useCallback(
    (result: SchemaDriftImpactApplyResult) => {
      // This job's own rerun shows under Current Run; the others run in their pipelines.
      const mine = result.runs.find((r) => r.jobId === jobId);
      if (mine) {
        const stub = queuedRunStub(mine);
        setActiveRun(stub);
        setRuns((prev) => [stub, ...prev]);
      }
      onJobChanged?.();
    },
    [jobId, onJobChanged],
  );

  const handleDriftRejected = useCallback(() => {
    setDriftCheck(null);
    setDriftMsg("Proposal rejected; nothing was changed.");
  }, []);

  const isRunning = activeRun !== null && !isTerminalStatus(activeRun.status);
  const stopRequested = isRunning && Boolean(activeRun?.stopRequestedAt);

  return (
    <div className="space-y-5">
      {/* ── Action bar ──────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => void handleRunJob()}
          disabled={triggering || isRunning}
          className="flex items-center gap-1.5 px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {triggering || isRunning ? (
            <Loader2 size={13} className="animate-spin" aria-hidden="true" />
          ) : (
            <Play size={13} aria-hidden="true" />
          )}
          {isRunning ? "Running…" : "Run Job"}
        </button>

        {isRunning && (
          <button
            type="button"
            onClick={() => void handleStopRun()}
            disabled={stopping || stopRequested}
            title="Stop this run before its next write to the target table"
            className="flex items-center gap-1.5 px-3 py-2 bg-white/5 hover:bg-white/10 text-red-300 border border-red-500/20 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {stopping || stopRequested ? (
              <Loader2 size={13} className="animate-spin" aria-hidden="true" />
            ) : (
              <Square size={13} aria-hidden="true" />
            )}
            {stopRequested ? "Stopping…" : "Stop run"}
          </button>
        )}

        <button
          type="button"
          onClick={() => void handleRunAll()}
          disabled={runAllPending}
          className="flex items-center gap-1.5 px-3 py-2 bg-white/5 hover:bg-white/10 text-white/70 border border-white/10 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {runAllPending ? (
            <Loader2 size={13} className="animate-spin" aria-hidden="true" />
          ) : (
            <PlayCircle size={13} aria-hidden="true" />
          )}
          Run All Jobs
        </button>

        <button
          type="button"
          onClick={() => void handleCheckDrift()}
          disabled={checkingDrift || checkingImpact || isRunning}
          title="Compare the live source and target with this job's mapping"
          className="flex items-center gap-1.5 px-3 py-2 bg-white/5 hover:bg-white/10 text-amber-300 border border-amber-500/20 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {checkingDrift ? (
            <Loader2 size={13} className="animate-spin" aria-hidden="true" />
          ) : (
            <ScanSearch size={13} aria-hidden="true" />
          )}
          Check for drift
        </button>

        <button
          type="button"
          onClick={() => void handleCheckImpact()}
          disabled={checkingImpact || checkingDrift || isRunning}
          title="Find every job in this project that reads this job's source table (and every job downstream of them), and check each for drift"
          className="flex items-center gap-1.5 px-3 py-2 bg-white/5 hover:bg-white/10 text-amber-300 border border-amber-500/20 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {checkingImpact ? (
            <Loader2 size={13} className="animate-spin" aria-hidden="true" />
          ) : (
            <GitBranch size={13} aria-hidden="true" />
          )}
          Find every affected job
        </button>

        <select
          value={runMode}
          onChange={(e) => setRunMode(e.target.value as RunMode)}
          disabled={runAllPending}
          aria-label="Run mode for Run All Jobs"
          title="Choose how jobs run: parallel (all at once), sequential (one at a time, creation order), or custom order (one at a time, by the order set in the job list)"
          className="px-2 py-2 bg-white/5 hover:bg-white/10 text-white/70 border border-white/10 rounded-lg text-xs font-medium transition-colors focus:outline-none focus:ring-1 focus:ring-brand-500/30 disabled:opacity-50"
        >
          <option value="parallel">Parallel</option>
          <option value="sequential">Sequential</option>
          <option value="custom">Custom Order</option>
        </select>

        {runAllMsg !== null && (
          <span className="text-xs text-emerald-400">{runAllMsg}</span>
        )}
        {runAllError !== null && (
          <span className="text-xs text-red-400" role="alert">
            {runAllError}
          </span>
        )}
      </div>

      {/* ── Trigger error ────────────────────────────────────────────────────── */}
      {triggerError !== null && (
        <p className="text-red-400 text-xs" role="alert">
          {triggerError}
        </p>
      )}

      {/* ── Schema drift ─────────────────────────────────────────────────────── */}
      {driftError !== null && (
        <p className="text-red-400 text-xs" role="alert">
          {driftError}
        </p>
      )}
      {driftMsg !== null && (
        <p className="text-emerald-400 text-xs" role="status">
          {driftMsg}
        </p>
      )}
      {driftCheck !== null && (
        <SchemaDriftPanel
          check={driftCheck}
          onApplied={handleDriftApplied}
          onRejected={handleDriftRejected}
          onFindImpact={() => void handleCheckImpact()}
        />
      )}
      {impact !== null && (
        <SchemaDriftImpactPanel
          impact={impact}
          onApplied={handleImpactApplied}
          onClose={() => setImpact(null)}
          onRecheck={() => void handleCheckImpact()}
        />
      )}

      {/* ── Current run status ──────────────────────────────────────────────── */}
      {activeRun !== null && (
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-white/60">
              Current Run
            </span>
            <StatusChip status={activeRun.status} />
          </div>

          {activeRun.status === "cancelled" && activeRun.errorMessage && (
            <p className="text-xs text-amber-300" role="status">
              {activeRun.errorMessage}
            </p>
          )}

          {!isTerminalStatus(activeRun.status) && activeRun.stopRequestedAt && (
            <p className="text-xs text-white/50" role="status">
              Stopping: the run ends before its next write to the target. A
              write already in progress finishes first.
            </p>
          )}

          {activeRun.status === "failed" && activeRun.errorMessage && (
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-xs text-red-400" role="alert">
                {activeRun.errorMessage}
              </p>
              {isSchemaDriftError(activeRun.errorMessage) &&
                driftCheck === null && (
                  <button
                    type="button"
                    onClick={() => void handleCheckDrift()}
                    disabled={checkingDrift}
                    className="flex items-center gap-1 px-2 py-1 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 rounded text-xs font-medium transition-colors disabled:opacity-50"
                  >
                    <ScanSearch size={11} aria-hidden="true" />
                    Check for drift
                  </button>
                )}
            </div>
          )}

          {isTerminalStatus(activeRun.status) && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
                <MetricRow
                  label="Rows Extracted"
                  value={activeRun.rowsExtracted}
                />
                <MetricRow label="Rows Loaded" value={activeRun.rowsLoaded} />
                <MetricRow label="Rows Merged" value={activeRun.rowsMerged} />
                <MetricRow
                  label="Duration"
                  value={formatDuration(activeRun.durationSeconds)}
                />
              </div>

              {job.loadStrategy === "scd2" && (
                <div className="border-t border-white/10 pt-2 space-y-1">
                  <p className="text-xs text-white/40">SCD2 Metrics</p>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
                    <MetricRow
                      label="Rows Expired"
                      value={activeRun.rowsExpired}
                    />
                    <MetricRow
                      label="Rows New Versions"
                      value={activeRun.rowsNewVersions}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {activeRun.dqResults.length > 0 && (
            <div className="space-y-0.5 pt-1 border-t border-white/10">
              <p className="text-xs text-white/40 pb-1">DQ Results</p>
              {activeRun.dqResults.map((r, idx) => (
                <DQResultRow
                  key={`active-dq-${r.ruleName}-${idx}`}
                  result={r}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Run history table ────────────────────────────────────────────────── */}
      <div>
        <h3 className="text-xs font-medium text-white/50 mb-2">Run History</h3>

        {historyLoading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 size={18} className="animate-spin text-white/20" />
          </div>
        ) : runs.length === 0 ? (
          <p className="text-xs text-white/30 text-center py-6">No runs yet.</p>
        ) : (
          <div className="overflow-x-auto custom-scrollbar-x rounded-xl border border-white/10">
            <table className="w-full min-w-max">
              <thead>
                <tr className="bg-white/3">
                  <th className="py-2 px-3 text-left text-xs font-medium text-white/40">
                    Started
                  </th>
                  <th className="py-2 px-3 text-left text-xs font-medium text-white/40">
                    Strategy
                  </th>
                  <th className="py-2 px-3 text-left text-xs font-medium text-white/40">
                    Status
                  </th>
                  <th className="py-2 px-3 text-right text-xs font-medium text-white/40">
                    Rows Loaded
                  </th>
                  <th className="py-2 px-3 text-right text-xs font-medium text-white/40">
                    Duration
                  </th>
                  <th className="py-2 px-3 text-left text-xs font-medium text-white/40">
                    DQ
                  </th>
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => (
                  <HistoryRow
                    key={run.id}
                    run={run}
                    strategy={job.loadStrategy}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small helper sub-component
// ---------------------------------------------------------------------------

function MetricRow({
  label,
  value,
}: {
  label: string;
  value: number | string | null | undefined;
}) {
  return (
    <div className="flex justify-between text-white/50">
      <span>{label}</span>
      <span className="text-white/70 font-mono">{value ?? "—"}</span>
    </div>
  );
}
